/* ============================================================
   ANRML PREMIUM — premium-fx.js
   Scroll-driven visual effects layer. No dependencies.
   Respects prefers-reduced-motion. Native scroll (no transform
   wrapper) so sticky headers / sticky gallery keep working.
   ============================================================ */

(function () {
  'use strict';

  const $  = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => [...c.querySelectorAll(s)];
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = window.matchMedia('(max-width: 768px)').matches || window.matchMedia('(pointer: coarse)').matches;

  /* ── SCROLL PROGRESS BAR ────────────────────────────────── */
  function initProgress() {
    const bar = document.createElement('div');
    bar.className = 'fx-progress';
    bar.innerHTML = '<span class="fx-progress__fill"></span>';
    document.body.appendChild(bar);
    const fill = bar.firstElementChild;

    let ticking = false;
    function update() {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      const pct = max > 0 ? (h.scrollTop / max) : 0;
      fill.style.transform = `scaleX(${pct})`;
      ticking = false;
    }
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ── SCROLL REVEAL (stagger, repeats each time on screen) ── */
  function initReveal() {
    const els = $$('[data-reveal]');
    if (!els.length) return;

    if (reduce) { els.forEach(el => el.classList.add('is-in')); return; }

    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const el = entry.target;
        if (entry.intersectionRatio >= 0.15) {
          const delay = parseFloat(el.dataset.revealDelay || '0');
          el.style.transitionDelay = `${delay}ms`;
          el.classList.add('is-in');
        } else if (entry.intersectionRatio === 0) {
          // fully off-screen → reset so it animates again next time
          el.classList.remove('is-in');
          el.style.transitionDelay = '0ms';
        }
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: [0, 0.15] });

    els.forEach(el => io.observe(el));
  }

  /* ── AUTO-STAGGER GROUPS ────────────────────────────────── */
  // Any [data-reveal-group] auto-assigns incremental delays to its
  // [data-reveal] children so grids cascade in without manual markup.
  function initStaggerGroups() {
    $$('[data-reveal-group]').forEach(group => {
      const step = parseFloat(group.dataset.revealGroup) || 90;
      $$('[data-reveal]', group).forEach((child, i) => {
        if (!child.dataset.revealDelay) child.dataset.revealDelay = String(i * step);
      });
    });
  }

  /* ── PARALLAX (generic) ─────────────────────────────────── */
  // data-parallax="0.3"  → element drifts at 30% of scroll delta.
  function initParallax() {
    if (reduce || isMobile) return;
    const els = $$('[data-parallax]');
    if (!els.length) return;

    let ticking = false;
    const vh = () => window.innerHeight;

    function update() {
      const viewH = vh();
      els.forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > viewH + 200) return;
        const speed = parseFloat(el.dataset.parallax) || 0.2;
        const center = rect.top + rect.height / 2 - viewH / 2;
        el.style.transform = `translate3d(0, ${(-center * speed).toFixed(1)}px, 0)`;
      });
      ticking = false;
    }
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ── HERO PARALLAX (zoom out + drift + fade) ────────────── */
  function initHero() {
    if (reduce || isMobile) return;
    const heroes = $$('.rep-hero');
    if (!heroes.length) return;

    let ticking = false;
    const vh = () => window.innerHeight;

    function update() {
      const viewH = vh();
      heroes.forEach(hero => {
        const rect = hero.getBoundingClientRect();
        if (rect.bottom < -100 || rect.top > viewH + 100) return;
        const media   = $('.rep-hero__media', hero);
        const content = $('.rep-hero__content', hero);
        const h = hero.offsetHeight || 1;
        // progress: -1 (entering from below) → 0 (centered) → 1 (leaving top)
        const p = Math.min(Math.max(-rect.top / h, -1), 1);
        const pp = Math.max(p, 0); // only fade/zoom on the way out
        if (media)   media.style.transform = `scale(${1.05 + pp * 0.16}) translate3d(0, ${p * 6}%, 0)`;
        if (content) {
          content.style.transform = `translate3d(0, ${pp * 40}px, 0)`;
          content.style.opacity = String(1 - pp * 1.15);
        }
      });
      ticking = false;
    }
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ── MAGNETIC BUTTONS ───────────────────────────────────── */
  function initMagnetic() {
    if (reduce || window.matchMedia('(pointer: coarse)').matches) return;
    $$('[data-magnetic]').forEach(el => {
      const strength = parseFloat(el.dataset.magnetic) || 0.35;
      el.addEventListener('mousemove', (e) => {
        const r = el.getBoundingClientRect();
        const x = e.clientX - r.left - r.width / 2;
        const y = e.clientY - r.top - r.height / 2;
        el.style.transform = `translate(${x * strength}px, ${y * strength}px)`;
      });
      el.addEventListener('mouseleave', () => { el.style.transform = ''; });
    });
  }

  /* ── TEXT REVEAL (mask wipe) ────────────────────────────── */
  function initTextReveal() {
    const els = $$('[data-text-reveal]');
    if (!els.length) return;

    els.forEach(el => {
      if (el.dataset.trReady) return;
      const txt = el.textContent;
      el.innerHTML = `<span class="fx-tr__inner">${txt}</span>`;
      el.classList.add('fx-tr');
      el.dataset.trReady = '1';
    });

    if (reduce) { els.forEach(el => el.classList.add('is-in')); return; }

    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.intersectionRatio >= 0.4) {
          entry.target.classList.add('is-in');
        } else if (entry.intersectionRatio === 0) {
          entry.target.classList.remove('is-in');
        }
      });
    }, { threshold: [0, 0.4] });
    els.forEach(el => io.observe(el));
  }

  /* ── REVEAL INMEDIATO DE LO QUE YA ESTÁ EN PANTALLA ──────
     Al entrar por un ancla (/#owners-club) el navegador salta antes de que los
     observers reaccionen, y la sección se queda en opacity:0 → se ve todo negro.
     Esto marca como visible cualquier elemento que ya esté en el viewport.
     Solo añade la clase que el observer pondría igual, así que es idempotente. */
  function revealInView() {
    const vh = window.innerHeight || document.documentElement.clientHeight;
    $$('[data-reveal],[data-text-reveal]').forEach(el => {
      const r = el.getBoundingClientRect();
      if (r.bottom > 0 && r.top < vh) {
        el.style.transitionDelay = '0ms';
        el.classList.add('is-in');
      }
    });
  }

  /* ── INIT ───────────────────────────────────────────────── */
  function init() {
    initProgress();
    initStaggerGroups();
    initReveal();
    initParallax();
    initHero();
    initMagnetic();
    initTextReveal();

    // Al entrar por un ancla el observer puede dejar la sección en opacity:0.
    // Un solo pase tras load, sin re-disparar scroll (eso congelaba la home).
    window.addEventListener('load', revealInView);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
